import { apiClient } from '../../../Utilities/apiClient';
import { User, UserStats, UsersResponse, UserFilters, UserRole, UserStatus } from '../../../Resources/Data/Users';

export const userService = {
  // Get user statistics (using admin endpoint)
  getUserStats: async (period: string = 'all'): Promise<{ data: { stats: UserStats } }> => {
    return apiClient.get(`/admin/users/stats/overview?period=${period}`);
  },

  // Get enhanced users list with pagination and filtering (using admin endpoint)
  getEnhancedUsers: async (filters: UserFilters): Promise<{ data: { users: UsersResponse } }> => {
    const params = new URLSearchParams();
    
    if (filters.name) params.append('name', filters.name);
    if (filters.email) params.append('email', filters.email);
    if (filters.phoneNumber) params.append('phoneNumber', filters.phoneNumber);
    if (filters.status) params.append('status', filters.status);
    if (filters.role) params.append('role', filters.role);
    if (filters.userType) params.append('userType', filters.userType);
    if (filters.isPremium) params.append('isPremium', filters.isPremium);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    return apiClient.get(`/admin/users?${params.toString()}`);
  },

  // Get single user by ID (using admin endpoint)
  getUserById: async (id: number): Promise<{ data: { user: User } }> => {
    return apiClient.get(`/admin/users/${id}`);
  },

  // Get detailed user information with all hikes and subscription details
  getUserDetails: async (id: number): Promise<{ 
    data: { 
      user: User;
      hikes: {
        started: any[];
        completed: any[];
        incomplete: any[];
      };
      subscription: {
        isPremium: boolean;
        premiumActivatedAt?: Date;
        premiumExpiresAt?: Date;
        daysRemaining?: number;
      };
    } 
  }> => {
    return apiClient.get(`/admin/users/${id}/details`);
  },
  
  // Revoke premium (still using user endpoint as it's not admin-specific)
  revokePremium: async (userId: number): Promise<void> => {
    await apiClient.patch(`/users/${userId}/revoke-premium`);
  },

  // Update user status (using admin endpoint)
  updateUserStatus: async (userId: number, status: UserStatus): Promise<{ data: { user: User } }> => {
    return apiClient.patch('/admin/users/status', { userId, status });
  },

  // Update user role (using admin endpoint)
  updateUserRole: async (userId: number, role: UserRole): Promise<{ data: { user: User } }> => {
    return apiClient.patch('/admin/users/role', { userId, role });
  },

  // Delete user (existing endpoint - users can delete themselves)
  deleteUser: async (userId: number): Promise<void> => {
    await apiClient.delete(`/users/delete-user/${userId}`);
  },

  // Get all users (using admin endpoint)
  getUsers: async (filters: UserFilters): Promise<{ data: { users: User[] } }> => {
    const params = new URLSearchParams();
    
    if (filters.name) params.append('name', filters.name);
    if (filters.email) params.append('email', filters.email);
    if (filters.phoneNumber) params.append('phoneNumber', filters.phoneNumber);
    if (filters.status) params.append('status', filters.status);
    if (filters.role) params.append('role', filters.role);
    if (filters.userType) params.append('userType', filters.userType);
    if (filters.isPremium) params.append('isPremium', filters.isPremium);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.pageSize) params.append('pageSize', filters.pageSize.toString());

    return apiClient.get(`/admin/users?${params.toString()}`);
  }
};

export default userService;